import { Area } from '@/modules/floorplanDesign/types';
import { assertNever } from '@/helpers/utils';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers';
import {
  HighwayArea,
  HighwayPoiSpec,
  LogicalSpace,
  Obstacle,
  Point,
  Rectangle,
  Segment,
} from '@thive/fddm-types';

import { ANGLE_EPSILON } from '@/helpers/angleConstants';

export type TransformSelectionMode = 'mirror-horizontal' | 'mirror-vertical' | 'rotate-90-cw';

export const transformAreaByMode = (
  area: Area,
  pivotX: number,
  pivotY: number,
  mode: TransformSelectionMode,
): Area => {
  if (isHighwayArea(area)) {
    const [firstSegment, ...otherSegments] = area.segments.map((segment) =>
      transformSegmentByMode(segment, pivotX, pivotY, mode),
    );

    if (!firstSegment) {
      return area;
    }

    return {
      ...area,
      segments: [firstSegment, ...otherSegments],
      highwayPoiSpecs: transformHighwayPoiSpecsByMode(area.highwayPoiSpecs, mode),
    };
  }

  return {
    ...area,
    rectangle: transformRectangleByMode(area.rectangle, pivotX, pivotY, mode),
  };
};

export const transformObstacleByMode = (
  obstacle: Obstacle,
  pivotX: number,
  pivotY: number,
  mode: TransformSelectionMode,
): Obstacle => {
  const [firstSegment, ...otherSegments] = obstacle.segments.map((segment) =>
    transformSegmentByMode(segment, pivotX, pivotY, mode),
  );

  if (!firstSegment) {
    return obstacle;
  }

  return {
    ...obstacle,
    segments: [firstSegment, ...otherSegments],
  };
};

export const transformLogicalSpaceByMode = (
  logicalSpace: LogicalSpace,
  pivotX: number,
  pivotY: number,
  mode: TransformSelectionMode,
): LogicalSpace => {
  const [pointA, pointB, pointC, ...otherPoints] = logicalSpace.polygon.points.map((point) =>
    transformPointByMode(point, pivotX, pivotY, mode),
  );

  if (!pointA || !pointB || !pointC) {
    return logicalSpace;
  }

  return {
    ...logicalSpace,
    polygon: {
      ...logicalSpace.polygon,
      points: [pointA, pointB, pointC, ...otherPoints],
    },
  };
};

const transformRectangleByMode = (
  rectangle: Rectangle,
  pivotX: number,
  pivotY: number,
  mode: TransformSelectionMode,
): Rectangle => {
  const center = transformPointByMode(
    {
      x: rectangle.centerX,
      y: rectangle.centerY,
    },
    pivotX,
    pivotY,
    mode,
  );

  const angle = normalizeAngle(getTransformedAngle(rectangle.angle, mode));

  return {
    ...rectangle,
    centerX: center.x,
    centerY: center.y,
    angle,
  };
};

const transformSegmentByMode = (
  segment: Segment,
  pivotX: number,
  pivotY: number,
  mode: TransformSelectionMode,
): Segment => {
  const start = transformPointByMode(segment.start, pivotX, pivotY, mode);
  const end = transformPointByMode(segment.end, pivotX, pivotY, mode);

  switch (mode) {
    case 'mirror-horizontal':
    case 'mirror-vertical':
      return {
        ...segment,
        leftSideWidth: segment.rightSideWidth,
        rightSideWidth: segment.leftSideWidth,
        start,
        end,
      };

    case 'rotate-90-cw':
      return {
        ...segment,
        start,
        end,
      };

    default:
      return assertNever(mode);
  }
};

const transformHighwayPoiSpecsByMode = (
  highwayPoiSpecs: HighwayArea['highwayPoiSpecs'],
  mode: TransformSelectionMode,
): HighwayArea['highwayPoiSpecs'] => {
  if (mode === 'rotate-90-cw') return highwayPoiSpecs;

  const [firstHighwayPoiSpec, ...otherHighwayPoiSpecs] = highwayPoiSpecs.map((highwayPoiSpec) =>
    transformHighwayPoiSpecByMode(highwayPoiSpec, mode),
  );

  if (!firstHighwayPoiSpec) return highwayPoiSpecs;

  return [firstHighwayPoiSpec, ...otherHighwayPoiSpecs];
};

const transformHighwayPoiSpecByMode = (
  highwayPoiSpec: HighwayPoiSpec,
  mode: TransformSelectionMode,
): HighwayPoiSpec => {
  const roadType = transformHighwayRoadTypeByMode(highwayPoiSpec.roadType, mode);

  if (roadType === highwayPoiSpec.roadType) return highwayPoiSpec;

  return {
    ...highwayPoiSpec,
    roadType,
  };
};

const transformHighwayRoadTypeByMode = (
  roadType: HighwayPoiSpec['roadType'],
  mode: TransformSelectionMode,
): HighwayPoiSpec['roadType'] => {
  switch (mode) {
    case 'mirror-horizontal':
    case 'mirror-vertical':
      return mirrorHighwayRoadType(roadType);

    case 'rotate-90-cw':
      return roadType;

    default:
      return assertNever(mode);
  }
};

const mirrorHighwayRoadType = (
  roadType: HighwayPoiSpec['roadType'],
): HighwayPoiSpec['roadType'] => {
  switch (roadType) {
    case 'ONE_WAY':
    case 'TWO_WAY_INDIA_STYLE':
      return roadType;

    case 'TWO_WAY_RIGHT_HAND_TRAFFIC':
      return 'TWO_WAY_LEFT_HAND_TRAFFIC';

    case 'TWO_WAY_LEFT_HAND_TRAFFIC':
      return 'TWO_WAY_RIGHT_HAND_TRAFFIC';

    default:
      return assertNever(roadType);
  }
};

const transformPointByMode = (
  point: Point,
  pivotX: number,
  pivotY: number,
  mode: TransformSelectionMode,
): Point => {
  switch (mode) {
    case 'mirror-horizontal':
      return {
        ...point,
        y: 2 * pivotY - point.y,
      };

    case 'mirror-vertical':
      return {
        ...point,
        x: 2 * pivotX - point.x,
      };

    case 'rotate-90-cw':
      return {
        ...point,
        x: pivotX + point.y - pivotY,
        y: pivotY - point.x + pivotX,
      };

    default:
      return assertNever(mode);
  }
};

const getTransformedAngle = (angle: number, mode: TransformSelectionMode): number => {
  switch (mode) {
    case 'mirror-horizontal':
      return Math.PI - angle;

    case 'mirror-vertical':
      return -angle;

    case 'rotate-90-cw':
      return angle - Math.PI / 2;

    default:
      return assertNever(mode);
  }
};

const normalizeAngle = (angle: number): number => {
  const normalizedAngle = Math.atan2(Math.sin(angle), Math.cos(angle));

  if (Math.abs(normalizedAngle) <= ANGLE_EPSILON) return 0;

  return normalizedAngle;
};
